import { Pagination } from "@payright/web-components";
import type {
  ISubTableRow,
  ITableRow,
  TableProps,
} from "@payright/web-components/dist/components/Tables";
import { Link } from "react-router-dom";
import * as React from "react";
import { defaultTransformer, sortRecord } from "./sort";
import { Paging } from "../../util/Paging";
import { RECORDS_PER_PAGE } from "../../util/constants/types";
import { forwardRef, useImperativeHandle } from "react";

export type TableWithPaginationProps<T> = {
  rawData: T[];
  Table: React.FC<TableProps>;
  // Inject component specific functionality
  mainRowTransformer?: (data: T) => ITableRow;
  subRowTransformer?: (data: T) => ITableRow;
  initialSortField: string;
} & Pick<TableProps, "tableHeaders" | "subTableHeaders">;

const TableWithPagination = forwardRef<{}, TableWithPaginationProps<any>>(
  (
    {
      initialSortField,
      rawData: initialRawData,
      mainRowTransformer = defaultTransformer,
      subRowTransformer,
      subTableHeaders,
      tableHeaders,
      Table,
    },
    ref
  ) => {
    const [currentPage, setCurrentPage] = React.useState(1);
    const [rawData, setRawData] = React.useState<ITableRow[]>(initialRawData);
    const [pageNumbers, setPageNumbers] = React.useState(0);
    const [{ tableRows, subTableRows }, setTableRows] = React.useState<{
      tableRows: ITableRow[];
      subTableRows?: ISubTableRow[];
    }>({
      tableRows: [],
      subTableRows: [],
    });

    useImperativeHandle(
      ref,
      () => {
        return {
          // This is used to export rows e.g. to ExportCSV
          getTableRows: () => [...(rawData ?? [])],
        };
      },
      [rawData]
    );

    React.useEffect(() => {
      setPageNumbers(Math.ceil(rawData.length / RECORDS_PER_PAGE));
      handleSortChange(initialSortField, "desc");
    }, []);


    const handleSortChange = (sortField: string, sortOrder: "asc" | "desc") => {
      const sortedRawData = rawData.sort(sortRecord(sortField, sortOrder, mainRowTransformer));
      const showTransaction = Paging(
        currentPage,
        RECORDS_PER_PAGE,
        sortedRawData
      );
  
      setRawData(sortedRawData);
      setTableRows({
        tableRows: showTransaction.map(mainRowTransformer),
        subTableRows: subRowTransformer && showTransaction.map(subRowTransformer),
      });
    };

    const handlePageChange = (selectedPage) => {
      setCurrentPage(Number(selectedPage.selected) + 1);
      const showTransaction = Paging(
        selectedPage.selected + 1,
        RECORDS_PER_PAGE,
        rawData
      );
  
      setTableRows({
        tableRows: showTransaction.map(mainRowTransformer),
        subTableRows: subRowTransformer && showTransaction.map(subRowTransformer),
      });
    };


    return (
      <>
        <div>
          <Table
            tableHeaders={tableHeaders}
            tableRows={tableRows}
            subTableHeaders={subTableHeaders}
            subTableRows={subTableRows}
            onSortChange={handleSortChange}
            LinkComponent={Link}
          />
        </div>
        <div>
          {pageNumbers > 1 && (
            <Pagination
              pageCount={pageNumbers}
              handlePageChange={handlePageChange}
            />
          )}
        </div>
      </>
    );
  }
);

export default TableWithPagination;
